import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  CreditCard,
  Search,
  Filter,
  Download,
  Calendar,
  DollarSign,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Clock,
} from "lucide-react";
import { EnhancedInvoice } from "@/types/invoice";

interface PaymentOverviewProps {
  invoices: EnhancedInvoice[];
  loading?: boolean;
}

const PaymentOverview: React.FC<PaymentOverviewProps> = ({
  invoices,
  loading = false,
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedPeriod, setSelectedPeriod] = useState("all");

  const formatCurrency = (amount: number) => {
    return `${amount.toFixed(3)} TND`;
  };

  const formatDate = (date: Date | string) => {
    const dateObj = typeof date === "string" ? new Date(date) : date;
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(dateObj);
  };

  // Calculate payment metrics
  const paymentMetrics = useMemo(() => {
    const totalInvoices = invoices.length;
    const totalAmount = invoices.reduce((sum, inv) => sum + inv.totalAmount, 0);
    const paidAmount = invoices.reduce(
      (sum, inv) => sum + (inv.paidAmount || 0),
      0
    );
    const outstandingAmount = invoices.reduce(
      (sum, inv) =>
        sum + (inv.remainingAmount || inv.totalAmount - (inv.paidAmount || 0)),
      0
    );

    const paidInvoices = invoices.filter(
      (inv) => inv.paymentStatus === "paid"
    ).length;
    const partialInvoices = invoices.filter(
      (inv) => inv.paymentStatus === "partial"
    ).length;
    const unpaidInvoices = invoices.filter(
      (inv) => inv.paymentStatus === "unpaid"
    ).length;
    const overdueInvoices = invoices.filter(
      (inv) => inv.isOverdue && inv.paymentStatus !== "paid"
    ).length;

    return {
      totalInvoices,
      totalAmount,
      paidAmount,
      outstandingAmount,
      paidInvoices,
      partialInvoices,
      unpaidInvoices,
      overdueInvoices,
      paymentRate: totalInvoices > 0 ? (paidAmount / totalAmount) * 100 : 0,
    };
  }, [invoices]);

  // Filter invoices based on search and filters
  const filteredInvoices = useMemo(() => {
    return invoices.filter((invoice) => {
      const matchesSearch =
        invoice.invoiceNumber
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        invoice.clientName.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus =
        selectedStatus === "all" || invoice.paymentStatus === selectedStatus;

      const matchesPeriod =
        selectedPeriod === "all" ||
        (() => {
          const invoiceDate = new Date(invoice.issueDate);
          const now = new Date();
          const daysDiff = Math.floor(
            (now.getTime() - invoiceDate.getTime()) / (1000 * 60 * 60 * 24)
          );

          switch (selectedPeriod) {
            case "7days":
              return daysDiff <= 7;
            case "30days":
              return daysDiff <= 30;
            case "90days":
              return daysDiff <= 90;
            default:
              return true;
          }
        })();

      return matchesSearch && matchesStatus && matchesPeriod;
    });
  }, [invoices, searchTerm, selectedStatus, selectedPeriod]);

  const getPaymentStatusBadge = (status: string, isOverdue: boolean) => {
    if (isOverdue && status !== "paid") {
      return (
        <Badge variant="destructive" className="flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" />
          {t("invoices.status.overdue")}
        </Badge>
      );
    }

    const statusConfig = {
      paid: {
        variant: "default" as const,
        icon: CheckCircle,
        className: "bg-green-500 hover:bg-green-600",
      },
      partial: {
        variant: "default" as const,
        icon: Clock,
        className: "bg-yellow-500 hover:bg-yellow-600",
      },
      unpaid: {
        variant: "destructive" as const,
        icon: AlertTriangle,
        className: "",
      },
    };

    const config =
      statusConfig[status as keyof typeof statusConfig] || statusConfig.unpaid;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className={config.className}>
        <Icon className="h-3 w-3 mr-1" />
        {t(`invoices.paymentStatus.${status}`)}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="animate-pulse space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-8 bg-gray-200 rounded w-1/2"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Payment Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("invoices.totalRevenue")}
            </CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(paymentMetrics.totalAmount)}
            </div>
            <p className="text-xs text-muted-foreground">
              {t("invoices.fromInvoices", {
                count: paymentMetrics.totalInvoices,
              })}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("invoices.collectedAmount")}
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(paymentMetrics.paidAmount)}
            </div>
            <p className="text-xs text-muted-foreground">
              {paymentMetrics.paymentRate.toFixed(1)}%{" "}
              {t("invoices.collectionRate")}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("invoices.outstandingAmount")}
            </CardTitle>
            <AlertTriangle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {formatCurrency(paymentMetrics.outstandingAmount)}
            </div>
            <p className="text-xs text-muted-foreground">
              {paymentMetrics.unpaidInvoices + paymentMetrics.partialInvoices}{" "}
              {t("invoices.pendingInvoices")}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t("invoices.overdueInvoices")}
            </CardTitle>
            <Clock className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {paymentMetrics.overdueInvoices}
            </div>
            <p className="text-xs text-muted-foreground">
              {t("invoices.requiresAttention")}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            {t("invoices.paymentOverview")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder={t("invoices.searchInvoices")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder={t("invoices.selectPaymentStatus")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("common.all")}</SelectItem>
                <SelectItem value="paid">
                  {t("invoices.paymentStatus.paid")}
                </SelectItem>
                <SelectItem value="partial">
                  {t("invoices.paymentStatus.partial")}
                </SelectItem>
                <SelectItem value="unpaid">
                  {t("invoices.paymentStatus.unpaid")}
                </SelectItem>
              </SelectContent>
            </Select>

            <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder={t("invoices.selectPeriod")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("invoices.allTime")}</SelectItem>
                <SelectItem value="7days">{t("invoices.last7Days")}</SelectItem>
                <SelectItem value="30days">
                  {t("invoices.last30Days")}
                </SelectItem>
                <SelectItem value="90days">
                  {t("invoices.last90Days")}
                </SelectItem>
              </SelectContent>
            </Select>

            <Button variant="outline" className="w-full sm:w-auto">
              <Download className="h-4 w-4 mr-2" />
              {t("invoices.exportPayments")}
            </Button>
          </div>

          {/* Payments Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("invoices.invoiceNumber")}</TableHead>
                  <TableHead>{t("invoices.client")}</TableHead>
                  <TableHead>{t("invoices.totalAmount")}</TableHead>
                  <TableHead>{t("invoices.paidAmount")}</TableHead>
                  <TableHead>{t("invoices.remainingAmount")}</TableHead>
                  <TableHead>{t("invoices.dueDate")}</TableHead>
                  <TableHead>{t("invoices.statusHeader")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInvoices.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="text-center py-8 text-muted-foreground"
                    >
                      {t("invoices.noPaymentsFound")}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredInvoices.map((invoice) => (
                    <TableRow key={invoice.id}>
                      <TableCell className="font-medium">
                        {invoice.invoiceNumber}
                      </TableCell>
                      <TableCell>{invoice.clientName}</TableCell>
                      <TableCell>
                        {formatCurrency(invoice.totalAmount)}
                      </TableCell>
                      <TableCell className="text-green-600">
                        {formatCurrency(invoice.paidAmount || 0)}
                      </TableCell>
                      <TableCell className="text-red-600">
                        {formatCurrency(
                          invoice.remainingAmount ||
                            invoice.totalAmount - (invoice.paidAmount || 0)
                        )}
                      </TableCell>
                      <TableCell>
                        {invoice.dueDate ? formatDate(invoice.dueDate) : "-"}
                      </TableCell>
                      <TableCell>
                        {getPaymentStatusBadge(
                          invoice.paymentStatus || "unpaid",
                          invoice.isOverdue || false
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentOverview;
